import { get } from 'lodash';
import React from 'react';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import { FieldMapping } from '../constants';

export type BatchListGroup = {
  parentTestId: string;
  label: string;
  children: any[];
  summaryChildren?: any[];
};

export function getBatchParentId(row: any) {
  return row?.parentTestId || get(row, 'testMetadata.parentTestId');
}

export function getBatchCaseId(row: any) {
  return row?.caseId || get(row, 'testMetadata.caseId');
}

export function getBatchLabel(row: any, parentTestId: string) {
  return row?.batchName || get(row, 'testMetadata.batchName') || parentTestId;
}

function getUniqueStrings(values: unknown[]) {
  return Array.from(
    new Set(
      values
        .flatMap(value => (Array.isArray(value) ? value : [value]))
        .map(value => String(value ?? '').trim())
        .filter(Boolean),
    ),
  );
}

function summarizeValues(values: string[], maxVisible: number) {
  const uniqueValues = values.filter(Boolean);
  if (uniqueValues.length === 0) return '-';
  if (uniqueValues.length <= maxVisible) return uniqueValues.join(', ');
  return `${uniqueValues.slice(0, maxVisible).join(', ')} +${
    uniqueValues.length - maxVisible
  }`;
}

function getOsSortValue(osv: string) {
  const numericValue = Number.parseFloat(osv);
  return Number.isFinite(numericValue) ? numericValue : Number.NEGATIVE_INFINITY;
}

function formatOsVersion(child: any) {
  const make = String(child?.make || '').trim();
  const osv = String(get(child, FieldMapping.osv) || '').trim();
  if (!osv) return null;

  if (make.toLowerCase() === 'wee') return `WEE${osv}`;
  return osv;
}

function getSortedOsVersions(children: any[]) {
  return getUniqueStrings(children.map(formatOsVersion)).sort((left, right) => {
    const leftIsWee = left.toUpperCase().startsWith('WEE');
    const rightIsWee = right.toUpperCase().startsWith('WEE');

    if (leftIsWee !== rightIsWee) return leftIsWee ? 1 : -1;
    if (leftIsWee && rightIsWee) return left.localeCompare(right);

    return getOsSortValue(right) - getOsSortValue(left);
  });
}

function getSortedCountries(children: any[]) {
  return getUniqueStrings(
    children.map(
      child =>
        get(child, 'testMetadata.country_code_3') ||
        get(child, FieldMapping.country),
    ),
  ).sort((left, right) => left.localeCompare(right));
}

function getFirstPresentValue(...values: unknown[]) {
  return values.find(value => String(value ?? '').trim().length > 0);
}

function getBatchAssetIdentity(child: any) {
  const primaryAssetId = getFirstPresentValue(
    get(child, 'testMetadata.primary_asset_id'),
    get(child, 'creative.primaryAssetId'),
  );
  if (primaryAssetId) return `primary:${primaryAssetId}`;

  const primaryAssetUrl = getFirstPresentValue(
    get(child, 'testMetadata.url'),
    get(child, 'creative.assets.0.url'),
  );
  if (primaryAssetUrl) return `url:${primaryAssetUrl}`;

  const creativeId = getFirstPresentValue(
    get(child, 'testMetadata.crid'),
    get(child, 'creativeMetadata.creativeId'),
    get(child, 'creative.id'),
  );
  if (creativeId) return `creative:${creativeId}`;

  return getFirstPresentValue(
    child?.assetLabel,
    get(child, 'testMetadata.assetLabel'),
    child?.caseId,
    get(child, 'testMetadata.caseId'),
  );
}

function getBatchAssetDisplayName(child: any) {
  const label = getFirstPresentValue(
    child?.assetLabel,
    get(child, 'testMetadata.assetLabel'),
    'Asset',
  );
  const primaryAssetId = getFirstPresentValue(
    get(child, 'testMetadata.primary_asset_id'),
    get(child, 'creative.primaryAssetId'),
  );

  return primaryAssetId ? `${label} (${primaryAssetId})` : String(label);
}

function getUniqueBatchAssets(children: any[]) {
  const assetsByIdentity = new Map<string, string>();

  children.forEach(child => {
    const identity = getBatchAssetIdentity(child);
    if (!identity) return;
    const key = String(identity);
    if (!assetsByIdentity.has(key)) {
      assetsByIdentity.set(key, getBatchAssetDisplayName(child));
    }
  });

  return Array.from(assetsByIdentity.values());
}

function formatBatchDate(value: unknown) {
  if (!value) return '-';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString();
}

function getBatchStatusStyles(status: string) {
  if (status.includes('Failed') || status === 'Error') {
    return { backgroundColor: '#fde8e8', color: '#b70303' };
  }
  if (status.includes('Verified') || status === 'Test Completed') {
    return { backgroundColor: '#d8f0f2', color: '#137f95' };
  }
  if (status === 'In Progress') {
    return { backgroundColor: '#ffe9c8', color: '#d08200' };
  }
  if (status === 'Not Picked') {
    return { backgroundColor: '#efe5fa', color: '#8d40a8' };
  }
  return { backgroundColor: '#efefef', color: '#6b6b6b' };
}

function BatchMetric({
  label,
  value,
  title,
}: {
  label: string;
  value: React.ReactNode;
  title?: string;
}) {
  return (
    <div
      style={{
        flex: '1 1 140px',
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
      }}
      title={title}
    >
      <span
        style={{
          color: '#000000',
          flexShrink: 0,
          fontSize: 13,
          fontWeight: 'bold',
        }}
      >
        {label}:
      </span>
      <span
        style={{
          color: '#000000',
          flex: 1,
          fontSize: 13,
          fontWeight: 400,
          marginLeft: 5,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </span>
    </div>
  );
}

export function BatchSummaryCard({
  group,
  expanded,
  onToggle,
}: {
  group: BatchListGroup;
  expanded: boolean;
  onToggle: () => void;
}) {
  const children = group.children;
  const summaryChildren = group.summaryChildren?.length
    ? group.summaryChildren
    : children;
  const firstChild = summaryChildren[0] || children[0] || {};
  const statuses: Record<string, number> = summaryChildren.reduce(
    (acc: Record<string, number>, child) => {
      const status = String(child?.status || 'Unknown');
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );
  const statusEntries = Object.entries(statuses).sort(
    ([, aCount], [, bCount]) => bCount - aCount,
  );
  const assets = getUniqueBatchAssets(summaryChildren);
  const adSlots = getUniqueStrings(
    summaryChildren.map(child => child?.adSlot || child?.testSlot),
  );
  const countries = getSortedCountries(summaryChildren);
  const osVersions = getSortedOsVersions(summaryChildren);
  const createdBy =
    get(firstChild, 'user.userName') ||
    firstChild?.createdBy ||
    get(firstChild, 'testMetadata.user.userName') ||
    '-';
  const createdAt = formatBatchDate(
    firstChild?.createdAt ||
      firstChild?.fetchTimestamp ||
      get(firstChild, 'testMetadata.createdAt'),
  );

  return (
    <div
      className="card"
      style={{
        width: '100%',
        minHeight: 128,
        marginTop: 12,
        marginBottom: expanded ? 0 : 12,
        backgroundColor: '#f0f6f7',
        border: '1px solid #b8d4d9',
        borderRadius: 6,
        boxShadow: 'none',
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        style={{
          width: '100%',
          minHeight: 128,
          border: 0,
          background: 'transparent',
          padding: '14px 68px 16px 18px',
          position: 'relative',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: 'lg-smart, sans-serif',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 16,
            marginBottom: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  color: '#852828',
                  fontSize: 13,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                Adsure Batch
              </span>
              <span
                style={{
                  backgroundColor: '#d8f0f2',
                  borderRadius: 999,
                  color: '#137f95',
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '4px 10px',
                  whiteSpace: 'nowrap',
                }}
              >
                {`${summaryChildren.length} test${
                  summaryChildren.length === 1 ? '' : 's'
                }`}
              </span>
            </div>
            <div
              title={group.parentTestId}
              style={{
                alignItems: 'center',
                color: '#000000',
                display: 'flex',
                fontSize: 13,
                maxWidth: 520,
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              <span style={{ flexShrink: 0, fontWeight: 'bold' }}>
                Batch ID:
              </span>
              <span
                style={{
                  flex: 1,
                  fontWeight: 400,
                  marginLeft: 5,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {group.parentTestId}
              </span>
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px 18px',
            marginBottom: 12,
          }}
        >
          <BatchMetric
            label="Creatives"
            value={assets.length}
            title={assets.join(', ')}
          />
          <BatchMetric
            label="Ad Slots"
            value={adSlots.length}
            title={adSlots.join(', ')}
          />
          <BatchMetric label="OS" value={summarizeValues(osVersions, 2)} />
          <BatchMetric label="Country" value={summarizeValues(countries, 3)} />
          <BatchMetric label="Created By" value={createdBy} />
          <BatchMetric label="Created" value={createdAt} />
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {statusEntries.map(([status, count]) => (
            <span
              key={status}
              style={{
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                lineHeight: 1,
                padding: '6px 10px',
                whiteSpace: 'nowrap',
                ...getBatchStatusStyles(status),
              }}
            >
              {status}: {count}
            </span>
          ))}
        </div>
        <span
          style={{
            alignItems: 'center',
            border: '1px solid #b8d4d9',
            borderRadius: 8,
            bottom: 14,
            color: '#006e7d',
            display: 'inline-flex',
            height: 34,
            justifyContent: 'center',
            position: 'absolute',
            right: 18,
            width: 34,
          }}
        >
          {expanded ? (
            <MdExpandLess size={24} aria-hidden />
          ) : (
            <MdExpandMore size={24} aria-hidden />
          )}
        </span>
      </button>
    </div>
  );
}
